/**
 * MultiChoiceQuiz Student Renderer
 * Student-style UI matching legacy quebon-tv-web styling.
 */

import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizImage, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import type { MultiChoiceQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useMultiChoiceQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { useQuizStyleVariant } from '../shared/quiz-style-context'
import { QUIZ_INTERACTIVE_STYLES } from '../shared/quiz-styles'

interface MultiChoiceStudentProps {
  quizId: string | number
  quiz: MultiChoiceQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 토글/피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 정답/내 답 토글은 유지하되 해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/이미지/보기 텍스트를 흐린다. */
  dimChoiceText?: boolean
}

export function MultiChoiceStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: MultiChoiceStudentProps) {
  const { t } = useTranslation('quiz')
  const variant = useQuizStyleVariant()
  const isAiq = variant === 'aiq'
  const ix = QUIZ_INTERACTIVE_STYLES[variant]

  const {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit,
  } = useMultiChoiceQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* subtype C일 때 데스크톱에서만 보기 개수만큼 컬럼 표시 */}
      {quiz.subtype === 'C' && (
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (min-width: 768px) {
              #${gridContainerId} {
                grid-template-columns: repeat(${sortedAnswers.length}, minmax(0, 1fr));
              }
            }
          `
        }} />
      )}
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.multiChoice.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimProblemText={dimChoiceText}
        dimMedia={dimChoiceText}
        problem={quiz.problem && (
          <div className={cn(isAiq ? 'text-[22px] font-medium leading-normal text-lc-text-primary' : 'text-xl leading-relaxed text-gray-900')}>
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      <ul
        id={quiz.subtype === 'C' ? gridContainerId : undefined}
        role="listbox"
        aria-label={t('types.multiChoice.selectOption')}
        aria-multiselectable="true"
        className={cn(
          'grid gap-4 mt-6',
          quiz.subtype === 'B' && 'grid-cols-1 md:grid-cols-2',
          quiz.subtype === 'C' && 'grid-cols-1',
          (!quiz.subtype || (quiz.subtype !== 'B' && quiz.subtype !== 'C')) && 'grid-cols-1'
        )}
      >
        {sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key)
          const isInResolvedCorrectAnswers = resolvedCorrectKeySet.has(answer.key)
          const isCorrectByCorrects = correctsBySelectedKey.get(answer.key)

          const showAsCorrect = isSolved && (
            shouldShowCorrectAnswers
              ? isInResolvedCorrectAnswers
              : isSelected && (isCorrectByCorrects !== undefined ? isCorrectByCorrects : isInResolvedCorrectAnswers)
          )

          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect
          const isDisplayedSelected = shouldShowCorrectAnswers
            ? isInResolvedCorrectAnswers
            : isSelected
          const isCompleted = isSolved || isViewSolved
          const hasImage = !!answer.image

          return (
            <li
              key={answer.key}
              role="option"
              aria-selected={isDisplayedSelected}
              onClick={() => !isCompleted && handleSelect(answer.key)}
              className={cn(
                'group w-full p-3 rounded-2xl border-2 text-[20px] font-medium text-left transition-all',
                'flex items-center cursor-pointer',
                // Text-only choices keep a comfortable tap target while growing for wrapped answers.
                !hasImage && 'min-h-16',
                // 기본 상태
                !isSolved && !isSelected && ix.optionIdle,
                !isSolved && !isSelected && ix.optionIdleHover,
                // 선택 상태 (풀이 전)
                !isSolved && isSelected && ix.optionSelected,
                // 정답
                showAsCorrect && 'text-lc-mint-900 bg-lc-mint-100 border-lc-mint-900 [box-shadow:unset]',
                // 오답
                isWrongSelected && 'text-lc-red-900 bg-lc-red-100 border-lc-red-900 [box-shadow:unset]',
                // 비활성화 (풀이 후 미선택/미정답)
                isSolved && !showAsCorrect && !isWrongSelected && 'border-lc-gray-400 text-gray-900 opacity-60',
                // 완료 상태
                isCompleted && 'pointer-events-none'
              )}
            >
              {/* 번호 배지 */}
              <div
                className={cn(
                  'w-8 h-8 mr-4 font-bold rounded-full shrink-0 text-[16px] flex items-center justify-center transition-all',
                  // 기본 상태
                  !isSelected && !isSolved && ix.badgeIdle,
                  !isSelected && !isSolved && ix.badgeIdleHover,
                  // 선택 상태 (풀이 전)
                  isSelected && !isSolved && ix.badgeSelected,
                  // 정답
                  showAsCorrect && 'text-white bg-lc-mint-900',
                  // 오답
                  isWrongSelected && 'text-white bg-lc-red-900',
                  // 비활성화
                  isSolved && !showAsCorrect && !isWrongSelected && 'text-lc-gray-500 bg-lc-gray-100'
                )}
              >
                {idx + 1}
              </div>

              {/* 보기 이미지 */}
              {answer.image && (
                <div className={cn(dimChoiceText && 'opacity-[0.05]')}>
                  <QuizImage
                    image={answer.image}
                    alt={t('types.multiChoice.optionAlt', { number: idx + 1 })}
                    className="my-0 shrink-0"
                  />
                </div>
              )}

              {/* 보기 내용 — G4: dimChoiceText 시 opacity 0.05 */}
              <div
                data-quiz-choice-text
                className={cn('flex-1 text-[20px] font-medium', dimChoiceText && 'opacity-[0.05]')}
              >
                <KatexText text={answer.answer} />
              </div>
            </li>
          )
        })}
      </ul>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) — G3: hideExplanation 시 정답 노출 차단을 위해 토글 자체를 억제 */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 — 특강 리뷰는 hideSolveDescription으로 토글만 유지한다. */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
